import { Component } from '@theme/component';

const STORAGE_KEY = 'age-verification-expires';

/**
 * A custom element that shows a full-screen age verification gate on page load
 * and remembers the visitor's confirmation for a configurable number of days.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} questionPanel - The panel asking the visitor's age.
 * @property {HTMLElement} rejectedPanel - The panel shown when the visitor is under age.
 * @property {HTMLInputElement} [rememberCheckbox] - The "remember me" checkbox.
 *
 * @extends Component<Refs>
 */
export class AgeVerificationPopup extends Component {
  requiredRefs = ['dialog', 'questionPanel', 'rejectedPanel'];

  connectedCallback() {
    super.connectedCallback();

    if (this.#isVerified()) return;

    // Defer the initial open by a couple of frames. Calling showModal() synchronously
    // while the browser is still settling a cross-document view-transition navigation
    // (used for regular page links, e.g. the Terms of Use / Privacy Policy pages linked
    // from this popup) can leave the dialog with the `open` attribute set but not
    // actually in the modal/top-layer state, so it renders with no ::backdrop and the
    // page behind it stays interactive.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#open());
    });
  }

  #open() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    dialog.addEventListener('cancel', this.#preventClose);
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
  }

  /**
   * Prevents the gate from being dismissed with the Escape key.
   *
   * @param {Event} event - The cancel event.
   */
  #preventClose = (event) => {
    event.preventDefault();
  };

  /**
   * Checks whether this visitor already confirmed their age.
   *
   * @returns {boolean}
   */
  #isVerified() {
    if (this.hasAttribute('test-mode')) return false;

    try {
      const expires = Number(localStorage.getItem(STORAGE_KEY) ?? '0');
      if (expires > Date.now()) return true;

      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  /**
   * Confirms the visitor meets the age requirement and closes the gate.
   */
  confirmAge() {
    const remember = this.refs.rememberCheckbox?.checked ?? false;
    const days = Number(this.getAttribute('remember-days')) || 30;

    if (!this.hasAttribute('test-mode')) {
      try {
        if (remember) {
          localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
        } else {
          sessionStorage.setItem(STORAGE_KEY, 'true');
        }
      } catch (error) {
        // Storage may be unavailable (private browsing, disabled cookies, etc).
      }
    }

    this.#close();
  }

  /**
   * Shows the rejection panel for visitors who don't meet the age requirement.
   */
  denyAge() {
    this.refs.questionPanel.hidden = true;
    this.refs.rejectedPanel.hidden = false;
  }

  /**
   * Returns from the rejection panel back to the age question.
   */
  resetChoice() {
    this.refs.rejectedPanel.hidden = true;
    this.refs.questionPanel.hidden = false;
  }

  #close() {
    const { dialog } = this.refs;

    dialog.removeEventListener('cancel', this.#preventClose);
    document.documentElement.style.overflow = '';
    dialog.close();
  }
}

if (!customElements.get('age-verification-popup')) {
  customElements.define('age-verification-popup', AgeVerificationPopup);
}
